import type Alpine from "alpinejs";
import { isFullPageNavigation, type SwapDetail } from "./build-skew-guard";

/**
 * Global navigation progress bar — a thin copper bar at the top of the viewport
 * for boosted (smooth) full-page navigations, so a slow page is never a blind
 * pause. See STYLING.md → "Loading states".
 *
 * Scope: ONLY full-page navigations (isFullPageNavigation: boosted GETs and GETs
 * targeting <body> — HX-Location follow-ups, htmx.ajax body refreshes) plus
 * htmx's Back/Forward refetch (historyCacheSize is 0, so every popstate is a
 * history cache miss that htmx loads with its OWN XHR and never fires
 * beforeRequest/beforeSend for). Autosaves, polling, fragment refreshes and SSE
 * never touch it.
 *
 * Behaviour:
 *   - nothing is shown for the first SHOW_DELAY (150ms): fast navigations never
 *     flash a bar;
 *   - then it trickles forward asymptotically towards 90% (nextTrickle);
 *   - when the last in-flight navigation ends it jumps to 100% and fades out;
 *   - overlapping navigations share ONE bar (in-flight set keyed by the XHR);
 *     finishing a key that was never started is a no-op, so the listener can
 *     call finish() on every terminal htmx event without re-classifying it;
 *   - a safety timeout finishes everything, so the bar can never get stuck;
 *   - prefers-reduced-motion: a static bar, no trickle, removed instantly;
 *   - it stands down while `suppressed()` (the CMS editor's own page-switch
 *     overlay, $store.cmsNav.navigating) so there is never double feedback.
 *
 * a11y: the page region is marked aria-busy="true" for the whole navigation
 * (immediately, not after the visual delay — it is invisible and accurate). The
 * bar is role="progressbar" with a localized aria-label and NO aria-valuenow:
 * the trickle values are fake, so exposing numbers would be misleading and
 * chatty. It is `hidden` (out of the a11y tree) whenever it is not shown. No
 * live-region announcement — a navigation is not an event worth interrupting a
 * screen-reader user for.
 */

export const NAV_PROGRESS_DEFAULTS = {
  /** Below this, a navigation feels instant — never show a bar for it. */
  showDelayMs: 150,
  trickleIntervalMs: 300,
  /** Covers the jump-to-100% + opacity fade in _nav-progress.css. */
  fadeOutMs: 350,
  /** Hard ceiling: a lost terminal event can never leave the bar stuck. */
  safetyTimeoutMs: 20_000,
} as const;

const INITIAL_PROGRESS = 0.08;
const TRICKLE_CAP = 0.9;
const TRICKLE_RATE = 0.1;
/** Reduced motion: a static full-width line while busy (no movement at all). */
const REDUCED_MOTION_PROGRESS = 1;

/** One trickle step: closes 10% of the remaining gap to the 90% cap. */
export function nextTrickle(p: number): number {
  if (p >= TRICKLE_CAP) return p;
  return p + (TRICKLE_CAP - p) * TRICKLE_RATE;
}

export interface NavProgressView {
  /** Make the bar visible at progress p (0..1). */
  show(p: number): void;
  setProgress(p: number): void;
  /** Jump to 100% and start the fade-out. */
  complete(): void;
  /** Fully hidden and reset. */
  hide(): void;
  setBusy(busy: boolean): void;
}

export interface NavProgressOptions {
  reducedMotion?: () => boolean;
  suppressed?: () => boolean;
  showDelayMs?: number;
  trickleIntervalMs?: number;
  fadeOutMs?: number;
  safetyTimeoutMs?: number;
}

type Timer = ReturnType<typeof setTimeout>;

export class NavProgressController {
  private inFlight = new Set<unknown>();
  private visible = false;
  private progress = 0;
  private showTimer: Timer | null = null;
  private trickleTimer: ReturnType<typeof setInterval> | null = null;
  private fadeTimer: Timer | null = null;
  private safetyTimer: Timer | null = null;
  private readonly opts: Required<NavProgressOptions>;

  constructor(
    private readonly view: NavProgressView,
    opts: NavProgressOptions = {},
  ) {
    this.opts = {
      reducedMotion: () => false,
      suppressed: () => false,
      ...NAV_PROGRESS_DEFAULTS,
      ...opts,
    };
  }

  get active(): boolean {
    return this.inFlight.size > 0;
  }

  start(key: unknown): void {
    const wasActive = this.active;
    this.inFlight.add(key);
    this.armSafety();
    if (wasActive) return; // overlapping navigation: keep the one bar going
    this.view.setBusy(true);
    if (this.fadeTimer) {
      // A new navigation while the previous bar fades out: restart it in place
      // (already on screen, so no delay) instead of letting it disappear.
      clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
      this.visible = false;
      this.reveal();
      this.startTrickle();
      return;
    }
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.reveal();
    }, this.opts.showDelayMs);
    this.startTrickle();
  }

  finish(key: unknown): void {
    if (!this.inFlight.delete(key)) return; // unknown / already finished
    if (this.active) return; // an overlapping navigation is still in flight
    this.complete();
  }

  finishAll(): void {
    if (!this.active) return;
    this.inFlight.clear();
    this.complete();
  }

  private complete(): void {
    this.clearTimers();
    this.view.setBusy(false);
    if (!this.visible) {
      // Fast navigation (or suppressed): nothing was ever shown.
      this.view.hide();
      return;
    }
    if (this.opts.reducedMotion()) {
      this.hideNow();
      return;
    }
    this.view.complete();
    this.fadeTimer = setTimeout(() => {
      this.fadeTimer = null;
      this.hideNow();
    }, this.opts.fadeOutMs);
  }

  private hideNow(): void {
    this.visible = false;
    this.progress = 0;
    this.view.hide();
  }

  /** Show the bar unless the CMS overlay (or another owner) has it covered. */
  private reveal(): void {
    if (this.opts.suppressed()) {
      if (this.visible) this.hideNow();
      return;
    }
    if (this.visible) return;
    this.visible = true;
    this.progress = this.opts.reducedMotion() ? REDUCED_MOTION_PROGRESS : INITIAL_PROGRESS;
    this.view.show(this.progress);
  }

  private startTrickle(): void {
    if (this.trickleTimer) return;
    this.trickleTimer = setInterval(() => {
      if (this.showTimer) return; // still inside the no-flash delay
      this.reveal(); // re-evaluates suppression every tick
      if (!this.visible || this.opts.reducedMotion()) return;
      this.progress = nextTrickle(this.progress);
      this.view.setProgress(this.progress);
    }, this.opts.trickleIntervalMs);
  }

  private armSafety(): void {
    if (this.safetyTimer) clearTimeout(this.safetyTimer);
    this.safetyTimer = setTimeout(() => {
      this.safetyTimer = null;
      this.finishAll();
    }, this.opts.safetyTimeoutMs);
  }

  private clearTimers(): void {
    if (this.showTimer) clearTimeout(this.showTimer);
    if (this.trickleTimer) clearInterval(this.trickleTimer);
    if (this.safetyTimer) clearTimeout(this.safetyTimer);
    if (this.fadeTimer) clearTimeout(this.fadeTimer);
    this.showTimer = this.trickleTimer = this.safetyTimer = this.fadeTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Public API for navigations that have no XHR yet.
//
// The listeners below key the bar by the htmx XHR. Work that precedes the XHR
// — a wiki navigation held at htmx:confirm while the editor saves
// (shared/wiki-nav.ts) — starts the bar under its OWN key (any unique object)
// and finishes that key when the hold ends. When the hold ends by issuing the
// request, start the XHR first and finish the held key after: the in-flight set
// is never empty in between, so the bar just continues (no restart flicker).
// finish() of an unknown/finished key is a no-op, like for XHR keys. Before
// registerNavProgress ran (tests, pages without the bundle) both are no-ops.
// ---------------------------------------------------------------------------

let sharedController: NavProgressController | null = null;

/** Installs the controller behind `navProgress` (registerNavProgress; tests). */
export function setNavProgressController(ctl: NavProgressController | null): void {
  sharedController = ctl;
}

export const navProgress = {
  start(key: object): void {
    sharedController?.start(key);
  },
  finish(key: object): void {
    sharedController?.finish(key);
  },
};

export const NAV_PROGRESS_ID = "nav-progress";

/**
 * DOM view over the #nav-progress element (templates/components/nav_progress.templ,
 * rendered with hx-preserve in the dashboard + focused layouts so it survives
 * body swaps). The element is looked up on every call: pages without it (public
 * Layout-only pages) simply get no bar.
 */
export function domNavProgressView(doc: Document = document): NavProgressView {
  const el = () => doc.getElementById(NAV_PROGRESS_ID);
  const setScale = (p: number) =>
    el()?.style.setProperty("--nav-progress", String(Math.max(0, Math.min(1, p))));
  // aria-busy on the swapped content region (falls back to <body>): the
  // sidebar/app chrome stays navigable while the page content is loading.
  const busyTarget = () => doc.getElementById("main-content") || doc.body;
  return {
    show(p) {
      const e = el();
      if (!e) return;
      e.dataset.state = "loading";
      setScale(p);
      e.hidden = false;
    },
    setProgress: setScale,
    complete() {
      const e = el();
      if (!e) return;
      e.dataset.state = "done";
      setScale(1);
    },
    hide() {
      const e = el();
      if (!e) return;
      e.hidden = true;
      delete e.dataset.state;
      setScale(0);
    },
    setBusy(busy) {
      const t = busyTarget();
      if (busy) t?.setAttribute("aria-busy", "true");
      else {
        // The swap may have replaced #main-content — clear both candidates.
        doc.getElementById("main-content")?.removeAttribute("aria-busy");
        doc.body?.removeAttribute("aria-busy");
      }
    },
  };
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  } catch {
    return false;
  }
}

/** Terminal htmx events for a request (afterRequest covers error/abort/timeout
 * too; the others are belt-and-braces — finish() is idempotent). */
const FINISH_EVENTS = [
  "htmx:afterRequest",
  "htmx:afterSettle",
  "htmx:responseError",
  "htmx:sendError",
  "htmx:sendAbort",
  "htmx:timeout",
  // Back/Forward refetch (htmx 1.9.10 loadHistoryFromServer): Load fires right
  // before the synchronous swap, LoadError on a non-2xx/3xx (both carry the
  // xhr; historyRestore does not). A network failure fires nothing at all →
  // the safety timeout.
  "htmx:historyCacheMissLoad",
  "htmx:historyCacheMissLoadError",
];

export function registerNavProgress(alpine: typeof Alpine) {
  if ((window as any).__studioNavProgressRegistered) return;
  (window as any).__studioNavProgressRegistered = true;

  const ctl = new NavProgressController(domNavProgressView(), {
    reducedMotion: prefersReducedMotion,
    // The CMS editor shows its own full-viewport page-switch overlay
    // (#cms-nav-overlay, static/js/cms/editor.ts) — let it own the feedback.
    suppressed: () => {
      try {
        return (alpine.store("cmsNav") as { navigating?: boolean } | undefined)?.navigating === true;
      } catch {
        return false;
      }
    },
  });

  setNavProgressController(ctl);

  // beforeSend (not beforeRequest): it fires only once the request is really
  // going out — after every beforeRequest listener had its chance to cancel —
  // with the same detail, so a cancelled request can never start a bar.
  document.addEventListener("htmx:beforeSend", (evt: Event) => {
    const detail = (evt as CustomEvent).detail as SwapDetail & { xhr?: unknown };
    if (!detail?.xhr || !isFullPageNavigation(detail, document.body)) return;
    ctl.start(detail.xhr);
  });
  document.addEventListener("htmx:historyCacheMiss", (evt: Event) => {
    const xhr = (evt as CustomEvent).detail?.xhr;
    if (xhr) ctl.start(xhr);
  });
  for (const name of FINISH_EVENTS) {
    document.addEventListener(name, (evt: Event) => {
      const xhr = (evt as CustomEvent).detail?.xhr;
      if (xhr) ctl.finish(xhr);
    });
  }
}
